import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

// Shared across every instance so that with modals stacked (e.g. a milestone
// editor opened from within a project detail view), Escape closes only the
// top-most one — not every open modal at once.
const openInstances = [];

// The overlay/backdrop/Escape-to-close plumbing every modal in this app used
// to reimplement on its own — none of them wired up Escape, because there
// was no one shared place to do it. The dialog element itself (div or form,
// with its own onClick={stopPropagation}) stays the caller's — they differ
// too much in element type and styling to force through one shape here.
export default function ModalShell({
  onClose,
  overlayClassName,
  closeOnOverlayClick = true,
  usePortal = true,
  children,
}) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    openInstances.push(onCloseRef);
    return () => {
      const idx = openInstances.indexOf(onCloseRef);
      if (idx !== -1) openInstances.splice(idx, 1);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (openInstances[openInstances.length - 1] === onCloseRef) onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const content = (
    <div className={overlayClassName} onClick={closeOnOverlayClick ? onClose : undefined}>
      {children}
    </div>
  );

  return usePortal ? ReactDOM.createPortal(content, document.body) : content;
}
